import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../utils/storage';
import { loginComRest } from '../services/authService';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

const API_KEY = 'AIzaSyDbknyG-fT-o7FRvemmjVxiID8g9n7QcYk';

const AuthScreen: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrarDados, setLembrarDados] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [senhaError, setSenhaError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [tentativasErro, setTentativasErro] = useState(0);

  useEffect(() => {
    const carregarDadosSalvos = async () => {
      try {
        const emailSalvo = await storage.getItem('emailSalvo');
        const senhaSalva = await storage.getItem('senhaSalva');
        const lembrarSalvo = await storage.getItem('lembrarDados');

        if (emailSalvo && senhaSalva && lembrarSalvo === 'true') {
          setEmail(emailSalvo);
          setSenha(senhaSalva);
          setLembrarDados(true);
        }
      } catch (error) {
        console.error('Erro ao carregar dados salvos:', error);
      }
    };

    carregarDadosSalvos();
  }, []);

  const validarEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError('Por favor, insira seu e-mail');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('E-mail inválido');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validarSenha = (senha: string) => {
    if (!senha.trim()) {
      setSenhaError('Por favor, insira sua senha');
      return false;
    }
    if (senha.length < 6) {
      setSenhaError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    setSenhaError('');
    return true;
  };

  const handleLogin = async () => {
    setEmailError('');
    setSenhaError('');
    
    const isEmailValid = validarEmail(email);
    const isSenhaValid = validarSenha(senha);
    
    if (!isEmailValid || !isSenhaValid) {
      return;
    }

    setIsLoading(true);

    try {
      if (lembrarDados) {
        await Promise.all([
          storage.setItem('emailSalvo', email),
          storage.setItem('senhaSalva', senha),
          storage.setItem('lembrarDados', 'true')
        ]);
      } else {
        await Promise.all([
          storage.removeItem('emailSalvo'),
          storage.removeItem('senhaSalva'),
          storage.removeItem('lembrarDados')
        ]);
      }

      const data = await loginComRest(email, senha);

      if (!data.localId) {
        throw new Error('Não foi possível identificar sua conta. Por favor, verifique suas credenciais.');
      }

      await storage.setItem('userId', data.localId);
      console.log('💾 [AUTH] userId salvo:', data.localId);

      const userDoc = await getDoc(doc(db, 'usuarios', data.localId));
      const userData = userDoc.data() || {} as any;

      const tipoRaw = String(userData?.tipo || '');
      const tipoNormalized = tipoRaw
        ? tipoRaw.charAt(0).toUpperCase() + tipoRaw.slice(1).toLowerCase()
        : '';
      const tiposPermitidos = ['Motoboy', 'Empresa', 'Central', 'Dev'];

      if (tiposPermitidos.includes(tipoNormalized)) {
        if (tipoNormalized === 'Empresa') {
          await storage.setItem('empresaId', data.localId);
        }
        await storage.setItem('userType', tipoNormalized);
        console.log('[DEBUG] Auth - userType salvo:', tipoNormalized);

        // Recarregar a página para atualizar o estado de autenticação
        window.location.href = `/${tipoNormalized.toLowerCase()}`;
      } else {
        alert('Seu tipo de usuário não tem permissão para acessar o aplicativo. Entre em contato com o suporte.');
        setTentativasErro(prev => prev + 1);
      }
    } catch (error: any) {
      const novaTentativa = tentativasErro + 1;
      setTentativasErro(novaTentativa);

      let mensagemErro = 'Erro ao fazer login. Tente novamente.';
      
      if (error.message) {
        if (error.message.includes('INVALID_PASSWORD') || error.message.includes('EMAIL_NOT_FOUND')) {
          mensagemErro = 'E-mail ou senha incorretos.';
        } else if (error.message.includes('TOO_MANY_ATTEMPTS')) {
          mensagemErro = 'Muitas tentativas. Tente novamente mais tarde.';
        } else {
          mensagemErro = error.message;
        }
      }

      if (novaTentativa >= 3) {
        mensagemErro += ' Se o problema persistir, entre em contato com o suporte.';
      }

      alert(mensagemErro);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppSupport = () => {
    const phoneNumber = '5566984773661';
    const message = 'Olá, preciso de ajuda com o sistema de entregas.';
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      {/* Floating Elements Animation */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-blue-500/20 rounded-full blur-xl animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-cyan-500/20 rounded-full blur-xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>

      <div className="relative min-h-screen flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 lg:p-10 border border-white/20">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <img 
                  src="/logoheader1.png" 
                  alt="Rota Entregas" 
                  className="h-20 w-auto object-contain"
                />
              </div>
              <p className="text-gray-600">Sistema de Entregas</p>
            </div>

            <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-mail
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => validarEmail(email)}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-400 ${
                  emailError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="seu@email.com"
                disabled={isLoading}
              />
            </div>
            {emailError && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {emailError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={mostrarSenha ? 'text' : 'password'}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                onBlur={() => validarSenha(senha)}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all text-gray-900 placeholder-gray-400 ${
                  senhaError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="Sua senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {mostrarSenha ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {senhaError && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {senhaError}
              </p>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="lembrar"
              checked={lembrarDados}
              onChange={(e) => setLembrarDados(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="lembrar" className="ml-2 block text-sm text-gray-700 cursor-pointer">
              Lembrar dados
            </label>
          </div>

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>

          {tentativasErro >= 3 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-yellow-800">
                    Muitas tentativas falharam. Se o problema persistir, entre em contato com o suporte.
                  </p>
                  <button
                    onClick={handleWhatsAppSupport}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Falar com Suporte
                  </button>
                </div>
              </div>
            </div>
          )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/login')}
                className="w-full text-gray-600 hover:text-gray-800 text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;

